package org.lennycodes.gradematewebsite.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.onMouseEnter
import com.varabyte.kobweb.compose.ui.modifiers.onMouseLeave
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.window
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.lennycodes.gradematewebsite.components.style.ButtonGroupStyle
import org.lennycodes.gradematewebsite.components.style.EyebrowStyle
import org.lennycodes.gradematewebsite.components.style.GradeMateColors
import org.lennycodes.gradematewebsite.components.style.PrimaryButtonStyle
import org.lennycodes.gradematewebsite.components.style.ResponsiveRowStyle
import org.lennycodes.gradematewebsite.components.style.SecondaryButtonStyle
import org.lennycodes.gradematewebsite.components.style.SectionHeadingStyle
import org.lennycodes.gradematewebsite.components.style.SectionInnerStyle
import org.lennycodes.gradematewebsite.components.style.SectionStyle
import org.lennycodes.gradematewebsite.components.style.SectionSubtitleStyle
import org.lennycodes.gradematewebsite.components.style.toRgba

/** Same easing as the nav bar: quick start, long soft landing. */
private const val Ease = "cubic-bezier(0.22, 1, 0.36, 1)"

/** Fade-and-rise entrance, staggered by [delayMs]. Collapses to nothing for reduced motion. */
private fun Modifier.reveal(
    visible: Boolean,
    delayMs: Int,
    reduce: Boolean,
    fromX: Int = 0,
    fromY: Int = 24,
    fromScale: Double = 1.0
) = styleModifier {
    val delay = if (reduce) 0 else delayMs
    val duration = if (reduce) 0 else 900
    property("opacity", if (visible) "1" else "0")
    property(
        "transform",
        if (visible) "translate(0px, 0px) scale(1)" else "translate(${fromX}px, ${fromY}px) scale($fromScale)"
    )
    property("transition", "opacity ${duration}ms ease ${delay}ms, transform ${duration}ms $Ease ${delay}ms")
}

// ---------------------------------------------------------------------------
// Hero / Home
// ---------------------------------------------------------------------------

@Composable
fun HeroSection() {
    var mounted by remember { mutableStateOf(false) }
    var tick by remember { mutableStateOf(0) }
    var primaryHovered by remember { mutableStateOf(false) }
    var secondaryHovered by remember { mutableStateOf(false) }
    var visualHovered by remember { mutableStateOf(false) }

    // Respect "reduce motion": no entrance, no idle loops
    val reduce = remember { window.matchMedia("(prefers-reduced-motion: reduce)").matches }

    // One slow clock drives the idle motion (badge pulse, glow breathing, image float)
    LaunchedEffect(Unit) {
        delay(80)
        mounted = true
        if (!reduce) {
            while (true) {
                delay(1200)
                tick++
            }
        }
    }
    val pulse = tick % 2 == 0
    val floatUp = (tick / 2) % 2 == 0

    Box(
        SectionStyle
            .toModifier()
            .id("home")
            .styleModifier {
                property("position", "relative")
                property("overflow", "hidden")
                // Two soft pools of brand colour in the corners
                property(
                    "background-image",
                    "radial-gradient(60% 55% at 88% 12%, ${GradeMateColors.Primary.toRgba(alpha = 0.10f)}, transparent 70%), " +
                            "radial-gradient(45% 45% at 4% 96%, ${GradeMateColors.Primary.toRgba(alpha = 0.06f)}, transparent 70%)"
                )
            }
    ) {
        Box(
            ResponsiveRowStyle
                .toModifier()
                .then(SectionInnerStyle.toModifier())
                .styleModifier {
                    property("align-items", "center")
                    property("gap", "clamp(36px, 6vw, 88px)")
                }
        ) {

            // ---------------------------------------------------------------
            // HERO CONTENT
            // ---------------------------------------------------------------
            Column(
                Modifier
                    .fillMaxWidth()
                    .styleModifier {
                        property("max-width", "680px")
                    }
            ) {

                // Eyebrow as a badge with a breathing dot
                P(
                    EyebrowStyle
                        .toModifier()
                        .margin(0.px)
                        .reveal(mounted, 100, reduce)
                        .styleModifier {
                            property("display", "inline-flex")
                            property("align-items", "center")
                            property("gap", "10px")
                            property("width", "fit-content")
                            property("align-self", "flex-start")
                            property("padding", "7px 16px 7px 12px")
                            property("border-radius", "999px")
                            property("background-color", GradeMateColors.Primary.toRgba(alpha = 0.08f))
                            property("border", "1px solid ${GradeMateColors.Primary.toRgba(alpha = 0.18f)}")
                            property("letter-spacing", "0.14em")
                            property("text-transform", "uppercase")
                            property("font-weight", "600")
                        }
                        .toAttrs()
                ) {
                    Span(
                        attrs = Modifier
                            .styleModifier {
                                property("display", "inline-block")
                                property("width", "8px")
                                property("height", "8px")
                                property("border-radius", "50%")
                                property("background-color", "${GradeMateColors.Primary}")
                                property(
                                    "box-shadow",
                                    "0 0 0 ${if (pulse) 6 else 0}px ${GradeMateColors.Primary.toRgba(alpha = 0.18f)}"
                                )
                                property("transition", "box-shadow 1200ms ease-in-out")
                            }
                            .toAttrs()
                    )
                    Text("Grading made simple")
                }

                // Headline with the first phrase picked out in a gradient
                P(
                    SectionHeadingStyle
                        .toModifier()
                        .margin(top = 20.px, bottom = 20.px)
                        .reveal(mounted, 220, reduce)
                        .styleModifier {
                            property("max-width", "680px")
                            property("font-size", "clamp(38px, 5.4vw, 64px)")
                            property("letter-spacing", "-0.045em")
                            property("line-height", "1.02")
                        }
                        .toAttrs()
                ) {
                    Span(
                        attrs = Modifier
                            .styleModifier {
                                property(
                                    "background-image",
                                    "linear-gradient(100deg, ${GradeMateColors.Primary}, #60a5fa)"
                                )
                                property("-webkit-background-clip", "text")
                                property("background-clip", "text")
                                property("-webkit-text-fill-color", "transparent")
                                property("color", "transparent")
                            }
                            .toAttrs()
                    ) {
                        Text("Grade smarter.")
                    }
                    Text(" Spend less time on spreadsheets.")
                }

                P(
                    SectionSubtitleStyle
                        .toModifier()
                        .margin(0.px)
                        .reveal(mounted, 340, reduce)
                        .styleModifier {
                            property("max-width", "560px")
                            property("font-size", "clamp(16px, 1.6vw, 19px)")
                            property("line-height", "1.7")
                        }
                        .toAttrs()
                ) {
                    Text(
                        "A modern student grading and records platform " +
                                "designed for institutions operating under " +
                                "NACTVET regulations."
                    )
                }

                // Links styled as buttons: one anchor each, no <a> nested inside a <button>
                Box(
                    ButtonGroupStyle
                        .toModifier()
                        .margin(top = 36.px)
                        .reveal(mounted, 460, reduce)
                        .styleModifier {
                            property("gap", "14px")
                        }
                ) {

                    Link(
                        path = "#downloads",
                        text = "Download GradeMate",
                        modifier = PrimaryButtonStyle
                            .toModifier()
                            .onMouseEnter { primaryHovered = true }
                            .onMouseLeave { primaryHovered = false }
                            .styleModifier {
                                property("display", "inline-flex")
                                property("align-items", "center")
                                property("justify-content", "center")
                                property("height", "50px")
                                property("box-sizing", "border-box")
                                property("padding", "0 26px")
                                property("border-radius", "12px")
                                property("text-decoration", "none")
                                property("font-size", "15px")
                                property("font-weight", "600")
                                property("line-height", "1")
                                property("color", "#ffffff")
                                // Soft sheen over the brand colour
                                property(
                                    "background-image",
                                    "linear-gradient(135deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0) 60%)"
                                )
                                property("transform", if (primaryHovered) "translateY(-3px)" else "translateY(0)")
                                property(
                                    "box-shadow",
                                    if (primaryHovered) "0 18px 36px -10px rgba(37, 99, 235, 0.5)"
                                    else "0 8px 20px -8px rgba(37, 99, 235, 0.4)"
                                )
                                property(
                                    "transition",
                                    "transform 250ms $Ease, box-shadow 250ms ease"
                                )
                            }
                    )

                    Link(
                        path = "#docs",
                        text = "Explore documentation",
                        modifier = SecondaryButtonStyle
                            .toModifier()
                            .onMouseEnter { secondaryHovered = true }
                            .onMouseLeave { secondaryHovered = false }
                            .styleModifier {
                                property("display", "inline-flex")
                                property("align-items", "center")
                                property("justify-content", "center")
                                property("height", "50px")
                                property("box-sizing", "border-box")
                                property("padding", "0 26px")
                                property("border-radius", "12px")
                                property("text-decoration", "none")
                                property("font-size", "15px")
                                property("font-weight", "600")
                                property("line-height", "1")
                                property(
                                    "color",
                                    if (secondaryHovered) "${GradeMateColors.Primary}" else "${GradeMateColors.TextPrimary}"
                                )
                                property(
                                    "background-color",
                                    if (secondaryHovered) GradeMateColors.Primary.toRgba(alpha = 0.06f)
                                    else "${GradeMateColors.Surface}"
                                )
                                property(
                                    "border",
                                    "1px solid ${
                                        if (secondaryHovered) GradeMateColors.Primary.toRgba(alpha = 0.4f)
                                        else "${GradeMateColors.Border35}"
                                    }"
                                )
                                property("transform", if (secondaryHovered) "translateY(-3px)" else "translateY(0)")
                                property(
                                    "transition",
                                    "transform 250ms $Ease, background-color 200ms ease, border-color 200ms ease, color 200ms ease"
                                )
                            }
                    )
                }

                // Small trust/detail line, with a short rule leading into it
                Box(
                    Modifier
                        .margin(top = 28.px)
                        .reveal(mounted, 580, reduce)
                        .styleModifier {
                            property("display", "flex")
                            property("align-items", "center")
                            property("gap", "12px")
                        }
                ) {
                    Box(
                        Modifier.styleModifier {
                            property("width", "28px")
                            property("height", "1px")
                            property("background-color", "${GradeMateColors.Border35}")
                            property("flex-shrink", "0")
                        }
                    )
                    P(
                        Modifier
                            .margin(0.px)
                            .styleModifier {
                                property("font-size", "13px")
                                property("line-height", "1.5")
                                property("color", GradeMateColors.TextSecondary)
                                property("letter-spacing", "0.01em")
                            }
                            .toAttrs()
                    ) {
                        Text("Built for simpler, more reliable academic records.")
                    }
                }
            }

            // ---------------------------------------------------------------
            // PRODUCT VISUAL
            // ---------------------------------------------------------------
            Box(
                Modifier
                    .fillMaxWidth()
                    .reveal(mounted, 380, reduce, fromX = 48, fromY = 16, fromScale = 0.96)
                    .styleModifier {
                        property("display", "flex")
                        property("justify-content", "center")
                        property("align-items", "center")
                        property("position", "relative")
                    }
            ) {

                // Soft background glow, breathing slowly
                Box(
                    Modifier
                        .styleModifier {
                            property("position", "absolute")
                            property("width", "80%")
                            property("aspect-ratio", "1")
                            property("border-radius", "50%")
                            property(
                                "background",
                                "radial-gradient(circle, rgba(37,99,235,0.16) 0%, rgba(37,99,235,0) 70%)"
                            )
                            property("filter", "blur(24px)")
                            property("pointer-events", "none")
                            property("transform", if (pulse) "scale(1.08)" else "scale(0.96)")
                            property("transition", "transform 2400ms ease-in-out")
                        }
                )

                // The image floats gently and lifts on hover
                Box(
                    Modifier
                        .onMouseEnter { visualHovered = true }
                        .onMouseLeave { visualHovered = false }
                        .styleModifier {
                            property("position", "relative")
                            property("z-index", "1")
                            property("width", "100%")
                            property("max-width", "500px")
                            property(
                                "transform",
                                when {
                                    visualHovered -> "translateY(-12px) scale(1.02)"
                                    floatUp -> "translateY(-8px) scale(1)"
                                    else -> "translateY(0px) scale(1)"
                                }
                            )
                            property(
                                "transition",
                                if (visualHovered) "transform 500ms $Ease" else "transform 2400ms ease-in-out"
                            )
                        }
                ) {
                    Img(
                        src = "/welcome.jpeg",
                        attrs = Modifier
                            .width(100.percent)
                            .borderRadius(20.px)
                            .styleModifier {
                                property("height", "auto")
                                property("object-fit", "contain")
                                property("display", "block")
                                property(
                                    "box-shadow",
                                    if (visualHovered) "0 44px 90px -30px rgba(15, 23, 42, 0.5)"
                                    else "0 28px 70px -30px rgba(15, 23, 42, 0.38)"
                                )
                                property(
                                    "border",
                                    "1px solid rgba(148, 163, 184, 0.16)"
                                )
                                property("transition", "box-shadow 500ms ease")
                            }
                            .toAttrs()
                    )
                }
            }
        }
    }
}